import {WTRENDS_API_PATH, WTRENDS_API_TOKEN, WTRENDS_API_URL} from '#/env'
import {type TrendsScope} from '../components/TrendsScopeTabs'
import {
  type Trend,
  type TrendCardStyle,
  type TrendSection,
  type TrendSignalKey,
  type TrendSignalLevel,
  type TrendSignals,
  type TrendsResponse,
  type TrendVelocity,
} from './trends'

/**
 * Live client for the W Trends service.
 *
 *   Base URL:  EXPO_PUBLIC_WTRENDS_API_URL  (default https://trend.wsocial.cloud)
 *   Path:      EXPO_PUBLIC_WTRENDS_API_PATH (default
 *              /xrpc/eu.wsocial.unspecced.getExplore)
 *   Auth:      Authorization: Bearer <EXPO_PUBLIC_WTRENDS_API_TOKEN>
 *   Endpoint:  GET <base><path>?region=<GLOBAL|CC>
 *
 * The W AppView serves trends over an AT Protocol XRPC route
 * (`eu.wsocial.unspecced.getExplore`); the payload shape is unchanged from the
 * previous `/api/trends` host, so the adapter below needs no changes.
 *
 * We deliberately fetch by region only and never send the backend's `category`
 * filter — category filtering is done client-side. The client's three scope
 * tabs map on like so:
 *
 *   scope 'global'  → region=GLOBAL
 *   scope 'region'  → region=<selected country code, e.g. SE>
 *   scope 'foryou'  → no endpoint yet — handled upstream (`useTrends`), never
 *                     reaches this client.
 *
 * On any non-2xx response, missing token, or unexpected payload shape this
 * throws — `useTrends` surfaces that as an error state (there is intentionally
 * no mock fallback for live scopes).
 */

export class TrendsApiError extends Error {
  status?: number
  constructor(message: string, status?: number) {
    super(message)
    this.name = 'TrendsApiError'
    this.status = status
  }
}

export type FetchTrendsParams = {
  /** Only `global` and `region` reach the live API; `foryou` is skipped. */
  scope: Exclude<TrendsScope, 'foryou'>
  /** Country code, used when scope is `region`. */
  region?: string | null
  signal?: AbortSignal
}

/** Resolve the `region` query param from the client's scope/region model. */
function resolveRegionParam({
  scope,
  region,
}: Pick<FetchTrendsParams, 'scope' | 'region'>): string {
  if (scope === 'region' && region) return region
  return 'GLOBAL'
}

export async function fetchTrends({
  scope,
  region,
  signal,
}: FetchTrendsParams): Promise<TrendsResponse> {
  if (!WTRENDS_API_TOKEN) {
    throw new TrendsApiError(
      'W Trends token is not configured (EXPO_PUBLIC_WTRENDS_API_TOKEN).',
    )
  }

  const url = new URL(WTRENDS_API_PATH, WTRENDS_API_URL)
  url.searchParams.set('region', resolveRegionParam({scope, region}))

  let res: Response
  try {
    res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${WTRENDS_API_TOKEN}`,
        Accept: 'application/json',
      },
      signal,
    })
  } catch (err) {
    throw new TrendsApiError(
      `W Trends request failed: ${(err as Error).message}`,
    )
  }

  if (!res.ok) {
    throw new TrendsApiError(
      `W Trends responded ${res.status} ${res.statusText}`,
      res.status,
    )
  }

  let raw: unknown
  try {
    raw = await res.json()
  } catch {
    throw new TrendsApiError('W Trends returned a non-JSON response.')
  }

  return normalizeTrendsResponse(raw)
}

/**
 * Wire → client adapter.
 *
 * The client `TrendsResponse` model was designed to mirror the documented wire
 * shape (`.agents/repos/root/trends/trends-how-it-works.md`), so this
 * is deliberately light: it validates the envelope, normalizes the few known
 * divergences, and otherwise passes data through.
 *
 * This is the single place to adjust once the first real payload is captured —
 * if a field is renamed server-side (e.g. per-trend `prominence` vs. the
 * client's per-section `cardStyle`), fix it here rather than in the components.
 */
export function normalizeTrendsResponse(raw: unknown): TrendsResponse {
  if (!raw || typeof raw !== 'object') {
    throw new TrendsApiError('W Trends payload is not an object.')
  }
  const body = raw as Record<string, unknown>
  if (!Array.isArray(body.sections)) {
    throw new TrendsApiError('W Trends payload is missing a `sections` array.')
  }

  const sections: TrendSection[] = body.sections.map(normalizeSection)

  // The ranked "Top trends" block is NOT sent as a section. It's derived from
  // the top-level `ranking` list (`[{id, rank, score, title}]`) resolved
  // against the flat `trends` pool. Synthesize it here and pin it first.
  const topSection = buildTopSection(body)

  return {
    sections: topSection ? [topSection, ...sections] : sections,
    cursor: typeof body.cursor === 'string' ? body.cursor : undefined,
    generatedAt:
      typeof body.generatedAt === 'string' ? body.generatedAt : undefined,
    stale: typeof body.stale === 'boolean' ? body.stale : undefined,
    schemaVersion:
      typeof body.schemaVersion === 'number' ? body.schemaVersion : undefined,
  }
}

/** How many ranked trends make up the Top trends block. */
const TOP_TRENDS_COUNT = 5

/**
 * Build the ranked Top trends section from `ranking[:5]` + the flat `trends`
 * pool. Each ranked trend gets its `signals`/`velocity` mapped from its own
 * `ranking` object (see `mapSignals`/`mapVelocity`). Returns `null` when the
 * payload carries no usable ranking (older responses, or nothing resolved).
 */
function buildTopSection(body: Record<string, unknown>): TrendSection | null {
  const ranking = body.ranking
  const pool = body.trends
  if (!Array.isArray(ranking) || !Array.isArray(pool)) return null

  const byId = new Map<string, Record<string, unknown>>()
  for (const entry of pool) {
    const id = (entry as {id?: unknown})?.id
    if (typeof id === 'string') byId.set(id, entry as Record<string, unknown>)
  }

  const trends: Trend[] = []
  for (const rankEntry of ranking.slice(0, TOP_TRENDS_COUNT)) {
    const id = (rankEntry as {id?: unknown})?.id
    const rawTrend = typeof id === 'string' ? byId.get(id) : undefined
    if (!rawTrend) continue
    trends.push(buildRankedTrend(rawTrend, rankEntry))
  }
  if (trends.length === 0) return null

  // `layout`/`cardStyle` are nominal — TopTrendsSection picks the card by rank.
  return {type: 'top', layout: 'feed', cardStyle: 'hero', trends}
}

function buildRankedTrend(
  rawTrend: Record<string, unknown>,
  rankEntry: unknown,
): Trend {
  const trend = normalizeTrend(rawTrend)
  const rank =
    numberOrUndefined((rankEntry as {rank?: unknown})?.rank) ??
    numberOrUndefined(rawTrend.rank)
  return {
    ...trend,
    rank,
    signals: mapSignals(rawTrend),
    velocity: mapVelocity(rawTrend),
  }
}

/**
 * Wire `trend.ranking.signals` is an array `[{key, level, score}]`; the client
 * wants a `{search, social, media}` object of `{value, level}`. Drops malformed
 * or unknown-keyed entries.
 */
function mapSignals(
  rawTrend: Record<string, unknown>,
): TrendSignals | undefined {
  const arr = (rawTrend.ranking as {signals?: unknown} | undefined)?.signals
  if (!Array.isArray(arr)) return undefined
  const signals: TrendSignals = {}
  for (const entry of arr) {
    const key = (entry as {key?: unknown})?.key
    const level = (entry as {level?: unknown})?.level
    const score = (entry as {score?: unknown})?.score
    if (isSignalKey(key) && isSignalLevel(level) && typeof score === 'number') {
      signals[key] = {value: score, level}
    }
  }
  return Object.keys(signals).length > 0 ? signals : undefined
}

/**
 * Wire `trend.ranking.velocity` is `{sinceHours, growthPercent}` with either
 * field nullable (both `null` for brand-new trends — no Google velocity yet).
 * Only a fully-populated velocity drives the pill; anything else → `null`.
 */
function mapVelocity(rawTrend: Record<string, unknown>): TrendVelocity | null {
  const v = (rawTrend.ranking as {velocity?: unknown} | undefined)?.velocity
  if (!v || typeof v !== 'object') return null
  const growthPercent = (v as {growthPercent?: unknown}).growthPercent
  const sinceHours = (v as {sinceHours?: unknown}).sinceHours
  if (typeof growthPercent !== 'number' || typeof sinceHours !== 'number') {
    return null
  }
  return {growthPercent, sinceHours}
}

function isSignalKey(value: unknown): value is TrendSignalKey {
  return value === 'search' || value === 'social' || value === 'media'
}

function isSignalLevel(value: unknown): value is TrendSignalLevel {
  return value === 'high' || value === 'medium' || value === 'low'
}

function numberOrUndefined(value: unknown): number | undefined {
  return typeof value === 'number' ? value : undefined
}

function normalizeSection(raw: unknown): TrendSection {
  const section = (raw ?? {}) as Record<string, unknown>
  const trends = Array.isArray(section.trends)
    ? section.trends.map(normalizeTrend)
    : []

  return {
    type: (section.type as TrendSection['type']) ?? 'main',
    title: typeof section.title === 'string' ? section.title : undefined,
    subtitle:
      typeof section.subtitle === 'string' ? section.subtitle : undefined,
    layout: normalizeLayout(section.layout),
    cardStyle: normalizeCardStyle(section, trends),
    trends,
  }
}

/**
 * Normalize a trend. The backend sometimes returns capitalized category values
 * (e.g. "Culture") while the client's keys + translation map are lowercase, so
 * a mismatch made labels fall back to the untranslated raw value. Lower-casing
 * the category here keeps lookups, filtering, and i18n consistent.
 */
function normalizeTrend(raw: unknown): Trend {
  const trend = (raw ?? {}) as Trend
  const category =
    typeof trend.category === 'string'
      ? (trend.category.toLowerCase() as Trend['category'])
      : trend.category
  return {...trend, category}
}

/** `grid` has no design yet — fall back to feed (matches the doc). */
function normalizeLayout(raw: unknown): TrendSection['layout'] {
  switch (raw) {
    case 'carousel':
      return 'carousel'
    case 'single':
      return 'single'
    case 'grid':
    case 'feed':
    default:
      return 'feed'
  }
}

/**
 * The client picks a card per SECTION. If the server already sends
 * `cardStyle`, trust it; otherwise derive from the first trend's `prominence`
 * (the wire's per-trend field) and default to `compact`.
 */
function normalizeCardStyle(
  section: Record<string, unknown>,
  trends: Trend[],
): TrendCardStyle {
  const explicit = section.cardStyle
  if (
    explicit === 'hero' ||
    explicit === 'featured' ||
    explicit === 'compact'
  ) {
    return explicit
  }
  const prominence = (trends[0] as {prominence?: unknown} | undefined)
    ?.prominence
  if (
    prominence === 'hero' ||
    prominence === 'featured' ||
    prominence === 'compact'
  ) {
    return prominence
  }
  return 'compact'
}
